import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import _ from "lodash"
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    MenuItem,
    TextField,
    useMediaQuery,
    useTheme
} from "@mui/material"
import {
    Close,
    ContactPhoneOutlined,
    FamilyRestroomOutlined,
    HealthAndSafetyOutlined,
    PersonOutline,
    SchoolOutlined
} from "@mui/icons-material"
import { updateFields, resetForm } from "../../Redux/actions/formValue.action"
import {
    useCreateClassStudentMutation,
    useUpdateStudentDetailsMutation
} from "../../Redux/actions/studentAction"
import { useSnackbar } from "../../components/Snakebar"

const genders = ["Male", "Female", "Other"]
const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]

const requiredFields = {
    firstName: "First Name",
    gender: "Gender",
    dob: "Date of Birth",
    address: "Address",
    rollNumber: "Roll Number",
    academicYear: "Academic Year",
    fatherName: "Father Name",
    motherName: "Mother Name",
    parentPhone: "Parent Phone",
}

/*
 * The form, section by section. `key` is the field's name in the shared
 * Redux form state; `wide` makes a field span both columns.
 */
const sections = [
    {
        title: "Student information",
        icon: <PersonOutline />,
        fields: [
            { key: "firstName", label: "First name", autoComplete: "given-name" },
            { key: "lastName", label: "Last name", autoComplete: "family-name" },
            { key: "gender", label: "Gender", options: genders },
            { key: "dob", label: "Date of birth", type: "date", helper: "Also the student's first password" },
            { key: "bloodGroup", label: "Blood group", options: bloodGroups },
        ]
    },
    {
        title: "Contact information",
        icon: <ContactPhoneOutlined />,
        fields: [
            { key: "studentEmail", label: "Email address", type: "email", autoComplete: "email" },
            { key: "studentPhone", label: "Phone number", type: "tel", autoComplete: "tel" },
            { key: "address", label: "Address", multiline: true, wide: true, autoComplete: "street-address" },
        ]
    },
    {
        title: "Academic information",
        icon: <SchoolOutlined />,
        fields: [
            { key: "admissionNumber", label: "Admission number" },
            { key: "rollNumber", label: "Roll number", inputMode: "numeric" },
            { key: "class", label: "Class", readOnly: true },
            { key: "section", label: "Section" },
            { key: "academicYear", label: "Academic year", placeholder: "2026-2027" },
        ]
    },
    {
        title: "Parent / guardian",
        icon: <FamilyRestroomOutlined />,
        fields: [
            { key: "fatherName", label: "Father's name" },
            { key: "motherName", label: "Mother's name" },
            { key: "fatherOccupation", label: "Father's occupation" },
            { key: "motherOccupation", label: "Mother's occupation" },
            { key: "parentPhone", label: "Parent phone", type: "tel" },
            { key: "parentEmail", label: "Parent email", type: "email", helper: "Used when the student has no email" },
        ]
    },
    {
        title: "Emergency contact",
        icon: <HealthAndSafetyOutlined />,
        fields: [
            { key: "emergencyContact_1", label: "Contact name" },
            { key: "emergencyContact_2", label: "Contact number", type: "tel" },
        ]
    }
]

const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#475569" }
}

export default function AddStudentModal({
    openAddStudentModal = true,
    handleCloseAddSudent,
    selectedClass,
    isEdit = false,
    selectedStudent = null
}) {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const dispatch = useDispatch()
    const { showSnackbar } = useSnackbar()
    const data = useSelector((state) => state?.form?.state)

    const [errors, setErrors] = useState({})
    // Errors only show once the user has tried to save
    const [submitted, setSubmitted] = useState(false)

    const [createStudentApi, createStudentStatus] = useCreateClassStudentMutation()
    const [updateStudentApi, updateStudentStaus] = useUpdateStudentDetailsMutation()
    const saving = createStudentStatus.isLoading || updateStudentStaus.isLoading

    useEffect(() => {
        if (openAddStudentModal) {
            setErrors({})
            setSubmitted(false)
        }
    }, [openAddStudentModal])

    useEffect(() => {
        if (updateStudentStaus.isError) {
            const message = _.get(
                updateStudentStaus,
                "error.data.detail",
                "Error while update the student details!,Please try after sometime"
            )
            showSnackbar({ message, severity: "error" })
            updateStudentStaus.reset()
        } else if (updateStudentStaus.isSuccess) {
            const message = _.get(updateStudentStaus, "data.message", "Student details updated success!")
            showSnackbar({ message, severity: "success" })
            updateStudentStaus.reset()
            dispatch(resetForm())
            handleCloseAddSudent()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateStudentStaus.isSuccess, updateStudentStaus.isError])

    useEffect(() => {
        if (createStudentStatus.isError) {
            const message = _.get(
                createStudentStatus,
                "error.data.detail",
                "Error while creating the student. Please try again."
            )
            showSnackbar({ message, severity: "error" })
            createStudentStatus.reset()
        }

        if (createStudentStatus.isSuccess) {
            const message = _.get(createStudentStatus, "data.message", "Student admitted successfully!")
            showSnackbar({ message, severity: "success" })
            createStudentStatus.reset()
            dispatch(resetForm())
            handleCloseAddSudent()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createStudentStatus.isError, createStudentStatus.isSuccess])

    const validate = (values) => {
        const newErrors = {}
        Object.entries(requiredFields).forEach(([key, label]) => {
            if (!_.get(values, key, "")) {
                newErrors[key] = `${label} is required`
            }
        })
        return newErrors
    }

    const handleChange = (key, value) => {
        dispatch(updateFields({ [key]: value }))
        if (submitted) setErrors(validate({ ...data, [key]: value }))
    }

    function handleCreateStudent(event) {
        event?.preventDefault()
        if (saving) return

        const newErrors = validate(data)
        setErrors(newErrors)
        setSubmitted(true)

        if (Object.keys(newErrors).length > 0) {
            const firstKey = Object.keys(requiredFields).find((key) => newErrors[key])
            document.getElementById(`student-field-${firstKey}`)?.focus()
            return
        }
        if (!selectedClass) return

        const field = (key) => _.get(data, key, "")
        const dataApi = {
            class_ref: _.get(selectedClass, "id", ""),
            school_ref: _.get(selectedClass, "institution_ref", ""),
            name: `${field("firstName")} ${field("lastName")}`,
            father_name: field("fatherName"),
            mother_name: field("motherName"),
            dob: field("dob"),
            roll_no: field("rollNumber"),
            password: field("dob"),
            email: field("studentEmail") || field("parentEmail"),
            meta_data: {
                gender: field("gender"),
                bloodGroup: field("bloodGroup"),
                studentPhone: field("studentPhone"),
                Address: field("address"),
                admissionNumber: field("admissionNumber"),
                section: field("section"),
                academicYear: field("academicYear"),
                fatherOccupation: field("fatherOccupation"),
                motherOccupation: field("motherOccupation"),
                parentPhone: field("parentPhone"),
                emergencyContact_1: field("emergencyContact_1"),
                emergencyContact_2: field("emergencyContact_2")
            }
        }

        if (isEdit && selectedStudent) {
            updateStudentApi({ data: dataApi, student_id: selectedStudent.id })
        } else {
            createStudentApi(dataApi)
        }
    }

    const handleCancel = () => {
        dispatch(resetForm())
        handleCloseAddSudent()
    }

    const renderField = (config) => {
        const { key, label, options, type, multiline, wide, readOnly, placeholder, helper, autoComplete, inputMode } = config
        const required = key in requiredFields
        const error = submitted ? errors[key] : undefined
        const value = readOnly ? selectedClass?.class_name || "" : _.get(data, key, "") ?? ""

        return (
            <div key={key} className={wide ? "sm:col-span-2" : undefined}>
                <TextField
                    id={`student-field-${key}`}
                    fullWidth
                    size="small"
                    label={label}
                    required={required}
                    select={Boolean(options)}
                    type={type === "date" || type === "email" || type === "tel" ? type : "text"}
                    multiline={multiline}
                    minRows={multiline ? 3 : undefined}
                    placeholder={placeholder}
                    disabled={readOnly}
                    value={value}
                    onChange={(e) => handleChange(key, e.target.value)}
                    error={Boolean(error)}
                    helperText={error || helper}
                    autoComplete={autoComplete || "off"}
                    InputLabelProps={type === "date" ? { shrink: true } : undefined}
                    inputProps={inputMode ? { inputMode } : undefined}
                    sx={fieldSx}
                >
                    {options?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            </div>
        )
    }

    const errorCount = submitted ? Object.keys(errors).length : 0

    return (
        <Dialog
            open={openAddStudentModal}
            onClose={saving ? undefined : handleCloseAddSudent}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            scroll="paper"
            aria-labelledby="student-dialog-title"
            slotProps={{
                paper: {
                    component: "form",
                    noValidate: true,
                    onSubmit: handleCreateStudent,
                    sx: { borderRadius: fullScreen ? 0 : "16px", backgroundColor: "#f8fafc" }
                }
            }}
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
                <div className="min-w-0">
                    <h2 id="student-dialog-title" className="text-lg font-semibold text-slate-900 sm:text-xl">
                        {isEdit ? "Update student details" : "Add new student"}
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-500">
                        {selectedClass?.class_name ? (
                            <>
                                Class{" "}
                                <span className="font-medium text-slate-700">{selectedClass.class_name}</span>
                                {" · "}
                            </>
                        ) : null}
                        Fields marked * are required
                    </p>
                </div>
                <IconButton aria-label="Close" onClick={handleCloseAddSudent} disabled={saving} edge="end">
                    <Close />
                </IconButton>
            </div>

            {/* Body */}
            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6">
                {errorCount > 0 && (
                    <Alert severity="error" sx={{ borderRadius: "10px" }}>
                        Please fill in {errorCount} required field{errorCount === 1 ? "" : "s"}.
                    </Alert>
                )}

                {sections.map((section) => (
                    <section key={section.title} className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
                        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 [&>svg]:h-[18px] [&>svg]:w-[18px]">
                                {section.icon}
                            </span>
                            {section.title}
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {section.fields.map(renderField)}
                        </div>
                    </section>
                ))}
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-white px-5 py-3 sm:flex-row sm:justify-end sm:px-6">
                <Button
                    variant="text"
                    color="inherit"
                    onClick={handleCancel}
                    disabled={saving}
                    sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: "none", borderRadius: "10px", px: 3 }}
                >
                    {saving ? "Saving…" : isEdit ? "Update student" : "Add student"}
                </Button>
            </div>
        </Dialog>
    )
}
